import { computed, type Ref } from "vue";
import { storeToRefs } from "pinia";
import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
import { useResultsV2PopulationsStore } from "@stores/survey/resultsV2/resultsV2.populations";
import { useSurveyStore } from "@stores/survey/survey";
import { POPULATION_AUTO_COLORS, populationColor } from "@/lib/chartPalettes";
import { computeMetric, formatMetricValue, metricTypesFor, unitForType, type KpiMetric } from "@/composables/kpiMetric";

// Re-export the pure helpers so existing imports from this module keep working.
export { computeMetric, formatMetricValue } from "@/composables/kpiMetric";

function useMetricFits(metric: Ref<KpiMetric | null>) {
  const { availableCards } = storeToRefs(useResultsV2Store());
  return computed(() => {
    const qid = metric.value?.question_id;
    const meta = availableCards.value.find((c: { questions?: { id: number }[] }) => (c.questions || []).some((q) => q.id === qid));
    return !meta || metricTypesFor(meta.type).includes(metric.value!.type);
  });
}

// A results-view analysis card with a KPI sub_type (kpi / kpi_trend).
interface KpiCard {
  content?: {
    metric?: KpiMetric;
  };
  options?: {
    population?: string;
    populations?: string[];
    color?: string | null;
  };
}

/**
 * Single-number KPI card data: reduces content.metric against options.population,
 * reading directly from the already-fetched chartsData (no extra endpoint).
 */
export function useKpiData(card: Ref<KpiCard | null | undefined>) {
  const store = useResultsV2Store();
  const { chartsData, currentView } = storeToRefs(store);
  const { populations: viewPopulations } = storeToRefs(useResultsV2PopulationsStore());

  const surveyStore = useSurveyStore();
  const { areWeightsEnabled } = storeToRefs(surveyStore);

  const withWeights = computed(() => {
    if (!areWeightsEnabled.value) return false;
    return currentView.value?.options?.apply_weightings ?? true;
  });

  const metric = computed(() => card.value?.content?.metric || null);
  const population = computed(() => card.value?.options?.population || "all");

  const source = computed(() => {
    const qid = metric.value?.question_id;
    if (qid == null) return null;
    const qData = chartsData.value[qid];
    if (!qData) return null;
    return population.value === "all" ? qData : qData[`pop_sg_${population.value}`] || null;
  });

  const metricFits = useMetricFits(metric);
  const result = computed(() => (metricFits.value ? computeMetric(metric.value, source.value, withWeights.value) : null));

  const formatted = computed(() => (result.value ? formatMetricValue(result.value.value, result.value.unit) : "—"));

  // Accent color: the custom one if set, otherwise the selected population's color.
  const color = computed(
    () => card.value?.options?.color || populationColor(population.value, viewPopulations.value),
  );

  return { metric, result, formatted, color, withWeights };
}

/**
 * KPI trend card data: evaluates the (single) metric across the ordered
 * populations (= waves) and returns line-chart-ready series. X axis = the
 * populations, in the order saved in options.populations.
 */
export function useKpiTrendData(card: Ref<KpiCard | null | undefined>) {
  const store = useResultsV2Store();
  const { chartsData, currentView, savedPopulations } = storeToRefs(store);

  const surveyStore = useSurveyStore();
  const { areWeightsEnabled } = storeToRefs(surveyStore);

  const withWeights = computed(() => {
    if (!areWeightsEnabled.value) return false;
    return currentView.value?.options?.apply_weightings ?? true;
  });

  // A trend card carries exactly one metric, plotted across the waves.
  const metric = computed(() => card.value?.content?.metric || null);
  const isConfigured = computed(() => metric.value?.question_id != null);


  // Ordered population tokens = the X axis (the waves), chosen and ordered in the
  // config panel and stored on options.populations.
  const populationTokens = computed(() => {
    const saved = card.value?.options?.populations;
    return Array.isArray(saved) ? saved : [];
  });

  const categories = computed(() =>
    populationTokens.value.map((token) => {
      const sp = savedPopulations.value.find((s) => s.token === token);
      return sp?.name || token;
    }),
  );

  const metricFits = useMetricFits(metric);

  const series = computed(() => {
    const m = metric.value;
    if (!m || !metricFits.value) return [];
    const qData = chartsData.value[m.question_id ?? -1];
    const points = populationTokens.value.map((token) => {
      const source = qData?.[`pop_sg_${token}`];
      const r = computeMetric(m, source, withWeights.value);
      return r ? { value: r.value, n: r.n } : null;
    });
    return [
      {
        id: "m1",
        label: name.value,
        color: m.color || POPULATION_AUTO_COLORS[0],
        unit: unitForType(m.type),
        points,
      },
    ];
  });

  const hasData = computed(
    () => categories.value.length > 0 && series.value.some((s) => s.points.some((p) => p != null)),
  );

  return { metric, isConfigured, categories, series, hasData, populationTokens, withWeights };
}
